import React, { useState, useMemo } from "react";
import classNames from "classnames";
import { Fade } from "react-awesome-reveal";
import { useCurrencyRates } from "@app/components/common/currency-converter/useCurrencyRates";
import { Currency } from "@app/types/currencies";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { Converter } from "./components/converter/Converter";
import { ViewVariant } from "./enums";
import { AmountTables } from "./components/amount-tables/AmountTables";

type Props = {
  chartTitle?: string;
  footnote?: string;
  currencyTableTitle?: string;
  variant?: ViewVariant;
  defaultFromCurrency?: Currency;
  defaultToCurrency?: Currency;
};

export const CurrencyConverterChart: React.FC<Props> = ({
  chartTitle,
  footnote,
  currencyTableTitle,
  variant,
  defaultFromCurrency,
  defaultToCurrency,
}) => {
  const [baseCurrency, setBaseCurrency] = useState(
    defaultFromCurrency ?? Currency.CHF,
  );
  const [convertCurrency, setConvertCurrency] = useState(
    defaultToCurrency ?? Currency.EUR,
  );
  const rates = useCurrencyRates();

  const exchangeRates = useMemo(
    () => ({
      direct: rates[baseCurrency].rates[convertCurrency],
      reverse: rates[convertCurrency].rates[baseCurrency],
    }),
    [rates, baseCurrency, convertCurrency],
  );

  return (
    <>
      <Fade
        duration={DEFAULT_FADE_DURATION}
        triggerOnce
        className={classNames(
          "mt-20 flex justify-center px-5",
          "lg:mt-25 lg:px-16",
          "xl:mt-40 xl:px-24",
        )}
      >
        <div
          className={classNames(
            "bg-dark text-primary-content w-full rounded-lg px-8 py-15",
            "lg:px-10 lg:py-20",
            "xl:max-w-container xl:px-27 xl:py-30",
          )}
        >
          <Converter
            baseCurrency={baseCurrency}
            convertCurrency={convertCurrency}
            rates={rates}
            exchangeRate={exchangeRates.direct}
            footnote={footnote}
            chartTitle={chartTitle}
            setBaseCurrency={setBaseCurrency}
            setConvertCurrency={setConvertCurrency}
          />
        </div>
      </Fade>
      {variant === ViewVariant.CHART_WITH_TABLES ? (
        <AmountTables
          title={currencyTableTitle}
          baseCurrency={baseCurrency}
          convertCurrency={convertCurrency}
          rates={exchangeRates}
        />
      ) : null}
    </>
  );
};
